import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import IgnoredTray from "./IgnoredTray";
import { IgnoreButton, ignoreItem } from "./IgnoreButton";

const NAME_CATEGORIES = [
  { key: "nickname", label: "Nickname in last name", description: "e.g. 'Christopher (Chris) Craik' → first=Christopher last=Craik nickname=Chris" },
  { key: "business_suffix", label: "Business suffix in last name", description: "e.g. 'CJ SINGH REAL ESTATE PROF. CORP.' → last=SINGH business=Real Estate Prof. Corp." },
  { key: "all_caps", label: "ALL-CAPS names", description: "Title-case ALL-CAPS records" },
  { key: "designation_tail", label: "Realtor designations (®, ABR, SRS)", description: "Strip professional designation suffixes" },
  { key: "punct", label: "Stray Punctuation in Name", description: "Strip stray symbols (·, quotes, parens) from first or last name and re-orient when first name is only punctuation" },
];

export default function NamesSection({ counts, onChange }) {
  const [active, setActive] = useState("nickname");
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [busyAll, setBusyAll] = useState(false);
  // Bulk-select: keep a separate Set of checked realtor ids per category
  const [checkedByCat, setCheckedByCat] = useState({});
  const checked = checkedByCat[active] || new Set();
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0, errors: 0 });
  const [ignoreVersion, setIgnoreVersion] = useState(0);

  async function load(cat) {
    setRows(null);
    const { data } = await api.get(`/cleanup/name-fixes/${cat}`);
    setRows(data.rows || []);
    // Clear selections for the loaded category — any prior selections are stale
    setCheckedByCat((prev) => ({ ...prev, [cat]: new Set() }));
  }
  useEffect(() => { load(active); }, [active]);

  async function afterRestore() {
    await load(active);
    setIgnoreVersion((v) => v + 1);
    onChange?.();
  }

  async function ignoreRow(r) {
    setBusyId(r.id);
    try {
      await ignoreItem(
        { kind: "name_fix", realtor_id: r.id, category: active },
        { label: r.current?.name || "record", onUndo: afterRestore },
      );
      setRows((rs) => (rs || []).filter((x) => x.id !== r.id));
      const next = new Set(checked);
      next.delete(r.id);
      setCatChecked(active, next);
      setIgnoreVersion((v) => v + 1);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ignore failed");
    } finally { setBusyId(null); }
  }

  function setCatChecked(cat, next) {
    setCheckedByCat((prev) => ({ ...prev, [cat]: next }));
  }

  function toggleRow(id) {
    const next = new Set(checked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setCatChecked(active, next);
  }

  function toggleAll() {
    if (!rows) return;
    if (checked.size === rows.length) setCatChecked(active, new Set());
    else setCatChecked(active, new Set(rows.map((r) => r.id)));
  }

  async function applyRow(r) {
    setBusyId(r.id);
    try {
      await api.post(`/cleanup/name-fix`, {
        realtor_id: r.id,
        first_name: r.proposed.first_name,
        last_name: r.proposed.last_name,
        name: r.proposed.name,
        nickname: r.proposed.nickname,
        business_name: r.proposed.business_name,
      });
      setRows((rs) => (rs || []).filter((x) => x.id !== r.id));
      // Drop from selection too
      const next = new Set(checked);
      next.delete(r.id);
      setCatChecked(active, next);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusyId(null); }
  }
  async function applyAll() {
    if (!rows || rows.length === 0) return;
    setBusyAll(true);
    let ok = 0, fail = 0;
    for (const r of rows) {
      try {
        await api.post(`/cleanup/name-fix`, {
          realtor_id: r.id,
          first_name: r.proposed.first_name,
          last_name: r.proposed.last_name,
          name: r.proposed.name,
          nickname: r.proposed.nickname,
          business_name: r.proposed.business_name,
        });
        ok++;
      } catch (e) { fail++; }
    }
    toast.success(`Applied ${ok} fixes${fail ? `, ${fail} failed` : ""}`);
    setBusyAll(false);
    await load(active);
    onChange?.();
  }

  async function applyChecked() {
    if (!rows || checked.size === 0) return;
    const targets = rows.filter((r) => checked.has(r.id));
    setBulkBusy(true);
    setBulkProgress({ done: 0, total: targets.length, errors: 0 });
    let done = 0, errors = 0;
    for (const r of targets) {
      try {
        await api.post(`/cleanup/name-fix`, {
          realtor_id: r.id,
          first_name: r.proposed.first_name,
          last_name: r.proposed.last_name,
          name: r.proposed.name,
          nickname: r.proposed.nickname,
          business_name: r.proposed.business_name,
        });
      } catch (e) { errors++; }
      done++;
      setBulkProgress({ done, total: targets.length, errors });
    }
    setBulkBusy(false);
    setBulkConfirmOpen(false);
    toast.success(`Applied ${done - errors} of ${targets.length} fixes${errors ? `, ${errors} failed` : ""}`);
    await load(active);
    onChange?.();
  }

  const countMap = {
    nickname: counts.name_nickname || 0,
    business_suffix: counts.name_business_suffix || 0,
    all_caps: counts.name_all_caps || 0,
    designation_tail: counts.name_designation_tail || 0,
    punct: counts.name_punct || 0,
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {NAME_CATEGORIES.map((c) => (
          <button
            key={c.key}
            onClick={() => setActive(c.key)}
            className={`px-3 py-2 rounded-md text-sm min-h-[44px] transition ${
              active === c.key
                ? "bg-violet-600 text-white"
                : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
            data-testid={`name-tab-${c.key}`}
          >
            {c.label} <span className="ml-1 opacity-75">({countMap[c.key]})</span>
          </button>
        ))}
      </div>
      {rows === null ? <div className="text-sm text-slate-500">Loading…</div> : rows.length === 0 ? (
        <div className="text-sm text-slate-500">No records in this category.</div>
      ) : (
        <>
          {(() => {
            const allChecked = rows.length > 0 && checked.size === rows.length;
            const someChecked = checked.size > 0 && !allChecked;
            const masterState = allChecked ? true : someChecked ? "indeterminate" : false;
            return (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-slate-200">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={masterState}
                    onCheckedChange={toggleAll}
                    data-testid={`name-select-all-${active}`}
                    aria-label="Select all rows in this category"
                  />
                  <span className="text-sm text-slate-700">
                    Select all in this category ({checked.size}/{rows.length})
                  </span>
                </label>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    disabled={checked.size === 0 || bulkBusy}
                    onClick={() => setBulkConfirmOpen(true)}
                    data-testid={`name-apply-checked-${active}`}
                  >
                    Apply to Checked ({checked.size})
                  </Button>
                  <Button
                    variant="outline"
                    onClick={applyAll}
                    disabled={busyAll}
                    size="sm"
                    data-testid="name-apply-all"
                  >
                    {busyAll ? "Applying…" : `Apply all (${rows.length})`}
                  </Button>
                </div>
              </div>
            );
          })()}
          <div className="max-h-[500px] overflow-y-auto space-y-2 pr-1">
            {rows.map((r) => {
              const isChecked = checked.has(r.id);
              return (
                <div
                  key={r.id}
                  className={`rounded-md border bg-white p-3 transition ${
                    isChecked ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200"
                  }`}
                  data-testid={`name-fix-${r.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="pt-1">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => toggleRow(r.id)}
                        data-testid={`name-row-checkbox-${r.id}-${active}`}
                        aria-label={`Select ${r.current?.name || r.id}`}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-slate-500">{r.brokerage_name}</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-1 text-sm">
                        <DiffRow label="name" cur={r.current.name} prop={r.proposed.name} />
                        <DiffRow label="first" cur={r.current.first_name} prop={r.proposed.first_name} />
                        <DiffRow label="last" cur={r.current.last_name} prop={r.proposed.last_name} />
                        <DiffRow label="nickname" cur={r.current.nickname} prop={r.proposed.nickname} />
                        <DiffRow label="business" cur={r.current.business_name} prop={r.proposed.business_name} />
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        size="sm"
                        onClick={() => applyRow(r)}
                        disabled={busyId === r.id}
                        data-testid={`name-apply-${r.id}`}
                      >
                        {busyId === r.id ? "…" : "Apply"}
                      </Button>
                      <IgnoreButton
                        onClick={() => ignoreRow(r)}
                        disabled={busyId === r.id}
                        testId={`name-ignore-${r.id}`}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bulk-apply confirm */}
          <AlertDialog open={bulkConfirmOpen} onOpenChange={(open) => { if (!open && !bulkBusy) setBulkConfirmOpen(false); }}>
            <AlertDialogContent data-testid={`name-bulk-confirm-dialog-${active}`}>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Apply {checked.size} name fix{checked.size === 1 ? "" : "es"}?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Each selected row's suggested fix will be written to the realtor's
                  <code className="mx-1">first_name</code> /
                  <code className="mx-1">last_name</code> /
                  <code className="mx-1">nickname</code> /
                  <code className="mx-1">business_name</code> fields.
                  This cannot be undone (but you can edit individual records afterward in the Realtors page).
                </AlertDialogDescription>
              </AlertDialogHeader>
              {bulkBusy && (
                <div className="text-sm text-slate-600" data-testid={`name-bulk-progress-${active}`}>
                  Applying fix {bulkProgress.done} of {bulkProgress.total}…
                  {bulkProgress.errors > 0 && <span className="text-rose-600 ml-2">({bulkProgress.errors} failed)</span>}
                </div>
              )}
              <AlertDialogFooter>
                <AlertDialogCancel disabled={bulkBusy}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  disabled={bulkBusy}
                  onClick={applyChecked}
                  data-testid={`name-bulk-confirm-apply-${active}`}
                >
                  {bulkBusy ? "Applying…" : `Apply ${checked.size} fix${checked.size === 1 ? "" : "es"}`}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
      <IgnoredTray kind="name_fix" version={ignoreVersion} onRestored={afterRestore} />
    </div>
  );
}

function DiffRow({ label, cur, prop }) {
  const changed = (cur || "") !== (prop || "");
  return (
    <div className="flex items-baseline gap-2 text-xs sm:text-sm">
      <span className="text-slate-400 w-16 shrink-0">{label}</span>
      <span className={changed ? "line-through text-rose-500" : "text-slate-700"}>{cur || "—"}</span>
      {changed && (
        <>
          <span className="text-slate-300">→</span>
          <span className="text-emerald-700 font-medium">{prop || "—"}</span>
        </>
      )}
    </div>
  );
}
